import { localize } from '@vee-validate/i18n'
import en from '@vee-validate/i18n/dist/locale/en.json'
import {
  confirmed,
  email,
  length,
  max,
  max_value as maxValue,
  min,
  min_value as minValue,
  regex,
  required,
  size,
  url,
} from '@vee-validate/rules'
import isFQDN from 'validator/lib/isFQDN'
import isInt from 'validator/lib/isInt'
import isIP from 'validator/lib/isIP'
import isMobilePhone from 'validator/lib/isMobilePhone'
import { configure, defineRule } from 'vee-validate'

import { definePlugin } from '/@/app'

import { useI18nWithout } from './i18n'

const domainValidator = (value: string) => {
  return isFQDN(value)
}
const ipValidator = (value: string) => {
  return isIP(value, 4)
}

const subnetValidator = (value: string) => {
  if (value.includes('/')) {
    const [ip, mask] = value.split('/')
    return isIP(ip, 4) && isInt(mask, { min: 0, max: 32 })
  }
  return false
}

const isMobilePhoneValidator = (value: string) => {
  if (value) {
    return isMobilePhone(value)
  }
  return true
}

const password = (value: string) => {
  // Field is empty, should pass
  if (!value || !value.length) return true

  // Regex Desc: Must be at least 12 Characters, Must contain at least a Capital Letter, Number and Special Character (,.;!+%&/#$).
  if (!/^(?=.*[A-Z])(?=.*[0-9])(?=.*[_\W\s])[a-zA-Z0-9_\W\s]{12,}$/.test(value)) {
    return useI18nWithout('settings.new-password-help-text')
  }

  return true
}

const noEmptyLines = (value: string) => {
  if (value) {
    return !value.split('\n').some((line) => !line.trim())
  }
  return true
}

export default definePlugin(() => {
  // Add vee-validate rules
  defineRule('required', required)
  defineRule('min', min)
  defineRule('max', max)
  defineRule('email', email)
  defineRule('regex', regex)
  defineRule('url', url)
  defineRule('length', length)
  defineRule('confirmed', confirmed)
  defineRule('min_value', minValue)
  defineRule('max_value', maxValue)
  defineRule('size', size)

  configure({
    generateMessage: localize({
      en: {
        messages: en.messages,
        names: {
          organization_code: useI18nWithout('vee-validate.organization_code'),
          organizations_codes: useI18nWithout('vee-validate.organization_code'),
          organization_ids: useI18nWithout('vee-validate.organization_ids'),
          modules_codes: useI18nWithout('vee-validate.modules_code'),
          force_exists: useI18nWithout('vee-validate.force_exists'),
          short_code: useI18nWithout('vee-validate.short_code'),
          sectors_codes: useI18nWithout('vee-validate.sectors_codes'),
          phone_number: useI18nWithout('vee-validate.phone_number'),
          first_name: useI18nWithout('vee-validate.first_name'),
          last_name: useI18nWithout('vee-validate.last_name'),
          old_password: useI18nWithout('vee-validate.old_password'),
          new_password1: useI18nWithout('vee-validate.new_password'),
          new_password2: useI18nWithout('vee-validate.confirm_new_password'),
          otp_code: useI18nWithout('vee-validate.otp_code'),
          is_approved: useI18nWithout('vee-validate.is_approved'),
          asset_ids: useI18nWithout('vee-validate.asset_ids'),
          report_type: useI18nWithout('vee-validate.report_type'),
          delivery_preferences: useI18nWithout('vee-validate.delivery_preferences'),
          file_type: useI18nWithout('vee-validate.file_type'),
          issue_selection: useI18nWithout('vee-validate.issue_selection'),
          time_frame: useI18nWithout('vee-validate.time_frame'),
          parent_id: useI18nWithout('vee-validate.parent_id'),
          solution_title: useI18nWithout('vee-validate.solution_title'),
          solution_description: useI18nWithout('vee-validate.solution_description'),
          mitre_tactics: useI18nWithout('vee-validate.mitre_tactics'),
          short_description: useI18nWithout('vee-validate.short_description'),
          ref_code_prefix: useI18nWithout('vee-validate.ref_code_prefix'),
          source_platform: useI18nWithout('vee-validate.source_platform'),
          brand_name: useI18nWithout('vee-validate.brand_name'),
          brand_domain: useI18nWithout('vee-validate.brand_domain'),
          country_code: useI18nWithout('vee-validate.country_code'),
          language_code: useI18nWithout('vee-validate.language_code'),
          data_type: useI18nWithout('vee-validate.data_type'),
          incident_types: useI18nWithout('vee-validate.incident_types'),
          detectionDate: useI18nWithout('vee-validate.detection_date'),
          breachName: useI18nWithout('vee-validate.name'),
          exposed_data: useI18nWithout('vee-validate.exposed_data'),
          breachDate: useI18nWithout('vee-validate.breach_date'),
          assets_ids: useI18nWithout('vee-validate.assets_ids'),
          run_on_existing_findings: useI18nWithout(
            'vee-validate.run_on_existing_findings',
          ),
          advertisement_id: useI18nWithout('vee-validate.advertisement_id'),
          advertisement_img: useI18nWithout('vee-validate.advertisement_img'),
          account_url: useI18nWithout('vee-validate.account_url'),
          account_name: useI18nWithout('vee-validate.account_name'),
          brand_monitoring_asset_count: useI18nWithout(
            'vee-validate.brand_monitoring_asset_count',
          ),
          third_party_asset_count: useI18nWithout('vee-validate.third_party_asset_count'),
          executive_name_asset_count: useI18nWithout(
            'vee-validate.executive_name_asset_count',
          ),
          executive_email_asset_count: useI18nWithout(
            'vee-validate.executive_email_asset_count',
          ),
          executive_social_media_account_asset_count: useI18nWithout(
            'vee-validate.executive_social_media_account_asset_count',
          ),
          credit_card_monitoring_asset_count: useI18nWithout(
            'vee-validate.credit_card_monitoring_asset_count',
          ),
          external_attack_surface_management_entity_count: useI18nWithout(
            'vee-validate.external_attack_surface_management_entity_count',
          ),
          organization_user_count: useI18nWithout('vee-validate.organization_user_count'),
          organization_team_count: useI18nWithout('vee-validate.organization_team_count'),
          mssp_organizations_count: useI18nWithout(
            'vee-validate.mssp_organizations_count',
          ),
          organization_analyst_support_availability_period: useI18nWithout(
            'vee-validate.organization_analyst_support_availability_period',
          ),
          total_ias_credit: useI18nWithout('vee-validate.total_ias_credit'),

          total_data_sale_credit: useI18nWithout('vee-validate.total_data_sale_credit'),
          trademark_name: useI18nWithout('vee-validate.trademark_name'),
          brand_website: useI18nWithout('vee-validate.brand_website'),
          brand_physical_address: useI18nWithout('vee-validate.brand_physical_address'),
          brand_registration_country: useI18nWithout(
            'vee-validate.brand_registration_country',
          ),
          trademark_registration_number: useI18nWithout(
            'vee-validate.trademark_registration_number',
          ),
          trademark_office: useI18nWithout('vee-validate.trademark_office'),
          trademark_class: useI18nWithout('vee-validate.trademark_class'),
          trademark_infringement_description: useI18nWithout(
            'vee-validate.trademark_infringement_description',
          ),
          attachment_ids: useI18nWithout('vee-validate.attachment_ids'),
          social_media_takedown_attachments: useI18nWithout(
            'vee-validate.social_media_takedown_attachments',
          ),
          is_disclaimer_accepted: useI18nWithout('vee-validate.is_disclaimer_accepted'),
          job_title: useI18nWithout('vee-validate.job_title'),
          fullname: useI18nWithout('vee-validate.fullname'),
          takedown_brand_name: useI18nWithout('vee-validate.takedown_brand_name'),
          email_header: useI18nWithout('vee-validate.email_header'),
          type_of_evidence: useI18nWithout('vee-validate.type_of_evidence'),
          type_of_phishing: useI18nWithout('vee-validate.type_of_phishing'),
          email_evidence_attachments: useI18nWithout(
            'vee-validate.email_evidence_attachments',
          ),
          license_attachments: useI18nWithout('vee-validate.license_attachments'),
          phishing_takedown_draft_attachments: useI18nWithout(
            'vee-validate.phishing_takedown_draft_attachments',
          ),
          idp_user_id: useI18nWithout('vee-validate.idp_user_id'),
          single_sign_on_url: useI18nWithout('vee-validate.single_sign_on_url'),
          entity_id: useI18nWithout('vee-validate.entity_id'),
          user_email: useI18nWithout('vee-validate.user_email'),
          x509_public_certificate: useI18nWithout('vee-validate.x509_public_certificate'),
        },
      },
    }),
  })

  // Add custom rules and messages
  defineRule('ip', ipValidator)
  defineRule('subnet', subnetValidator)
  defineRule('domain', domainValidator)
  defineRule('mobilePhone', isMobilePhoneValidator)
  defineRule('noEmptyLines', noEmptyLines)
  defineRule('password', password)

  configure({
    generateMessage: localize('en', {
      messages: {
        ip: 'The {field} field must be a valid IP address.',
        subnet: 'The {field} field must be a valid subnet.',
        domain: 'The {field} field must be a valid domain.',
        mobilePhone: 'The {field} field must be a valid phone number.',
        noEmptyLines: 'The {field} field must not contain empty lines.',
      },
    }),
  })
})
